import type { SsrProps } from '@/core/features/app/ssr-props';

import React from 'react';

import { ErrorPage } from '@/core/features';
import { useDynamicPagePartialComponents } from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-partial-component-query';
import {
    useDynamicPageQueryData,
    useDynamicPageQueryFetching,
    useHydrateDynamicPageQueryData,
} from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-query';
import useDynamicPageQueryCacheHandler from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-query-cache-handler';
import {
    getDynamicPageErrorStatusCode,
    mergeComponentsTogether,
} from '@/core/features/a-dynamic-page/services/dynamic-page-service';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { useUnhandledInternalAppNativeLinkListener } from '@/core/features/link/app-native-link-forward-service';
import { logger } from '@/core/features/logger/logger';
import { useHydrateOverlayHistoryState } from '@/core/features/modals/overlay-history/overlay-history-state';
import Main from '@/core/features/page/main';
import useRouterInit from '@/core/features/router/router-init';
import usePageScrollRestoration from '@/core/features/scroll/page-scroll-restoration/use-page-scroll-restoration';
import TrackingContainer from '@/core/features/tracking/tracking-container';
import DynamicPage from '@/features/a-dynamic-page/components/dynamic-page';
import PageDataComponentsSwitch from '@/features/a-dynamic-page/components/page-data-components-switch';

export default function DynamicPageContainer(ssrProps: SsrProps) {
    useHydrateDynamicPageQueryData();
    const { data, error } = useDynamicPageQueryData();
    const { isFetching, isPending } = useDynamicPageQueryFetching();
    const { partialComponents } = useDynamicPagePartialComponents(data?.response?.components ?? []);

    const errorStatusCode = getDynamicPageErrorStatusCode(data?.response ?? null, error);

    if (!data?.response) {
        throw `No Response found in Dynamic Page Container ${errorStatusCode ? `Code: ${errorStatusCode}` : ''}`;
    }

    const { isApp } = useDeviceoutput();
    useRouterInit({ network: { isLoading: isPending } });
    usePageScrollRestoration();

    useHydrateOverlayHistoryState();
    useUnhandledInternalAppNativeLinkListener({ isApp });

    // currently, this has to be here so that the invalidation is only done by one observer
    useDynamicPageQueryCacheHandler({
        cacheControl: data?.response?.meta.cacheControl ?? null,
        identifier: data?.response?.['@id'] ?? null,
        isCachedResponse: data?.cached ?? false,
        isDynamicPageCacheDisabled: ssrProps.isDynamicPageCacheDisabled,
    });

    // Error logging
    React.useEffect(() => {
        if (isFetching) {
            return;
        }

        if (error) {
            logger.error(`Error in dynamic-page-container ${errorStatusCode ? `Code: ${errorStatusCode}` : ''}`, {
                additionalData: JSON.stringify(error),
                url: location.href,
            });
            return;
        }

        if (!data?.response) {
            logger.error(
                `No Response found in Dynamic Page Container ${errorStatusCode ? `Code: ${errorStatusCode}` : ''}`,
                {
                    additionalData: JSON.stringify({ data, error }),
                    url: location.href,
                },
            );

            return;
        }
    }, [data, error, errorStatusCode, isFetching]);

    const finalPageComponents = mergeComponentsTogether(data?.response.components ?? [], partialComponents);

    if (errorStatusCode && errorStatusCode !== 301 && errorStatusCode !== 302) {
        return (
            <Main>
                <ErrorPage
                    detail={'API'}
                    isPageLoading={false}
                    statusCode={errorStatusCode}
                />
            </Main>
        );
    }

    return (
        <>
            <PageDataComponentsSwitch components={finalPageComponents} />
            <DynamicPage
                components={finalPageComponents}
                isPageLoading={isFetching}
                meta={data?.response?.meta}
                ssrProps={ssrProps}
            />
            <TrackingContainer
                metaData={data?.response?.meta ?? null}
                url={ssrProps.ssrUrl}
            />
        </>
    );
}
